import { Component, computed, inject, Injector, OnDestroy, OnInit, signal } from '@angular/core';
import { AccountService } from '@frontendlib/common/services/account.service';
import { AlertService } from '@modules/alert/services/alert.service';
import { type AlertButtonConfig } from '@modules/alert/types/config';
import { BaseSidebarComponent } from '@modules/common/components/base-sidebar/base-sidebar.component';
import { mailboxTenantContext, OWNING_SPACE_ID } from '@modules/common/interceptors/spaces.interceptor';
import { type SidebarSharedRow } from '@modules/common/components/knowledge-items-rail/shared-placeholder.component';
import { pendingAfterDelay } from '@modules/common/components/rail-skeleton/rail-skeleton.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Application } from '@modules/common/types/application';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { moveItemsIntoContainer } from '@modules/common/utils/move-into-container.util';
import { workspaceColor, workspacePalette } from '@modules/common/utils/workspace-color';
import { DragData, DragDataTypes, getStringField } from '@modules/drag-n-drop/types/drag-data';
import { NotebooksService } from '@modules/notes/services/notebooks.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { NoteAssignmentFeedService } from '@modules/notes/collaboration/note-assignment-feed.service';
import { Note } from '@modules/notes/types/note';
import { Notebook } from '@modules/notes/types/notebook';
import { CROSS_WORKSPACE_DROP_TEXT } from '@modules/notes/types/cross-workspace-drop-text';
import { VirtualFolder } from '@modules/notes/types/virtual-folder';
import { SpacesService } from '@modules/settings/services/spaces.service';
import { Space } from '@modules/settings/types/space';
import { Team } from '@modules/settings/types/team';
import { notesPinnedMenu } from '@modules/notes/components/sidebar-context-menu/notes-rail-menu';
import { isStitchItemSummary } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { timer } from 'rxjs';
import { map, retry, switchMap, takeUntil, tap, timeout } from 'rxjs/operators';

const RAIL_IDENTITY_TIMEOUT_MS = 10_000;

/**
 * How long the rail waits before asking again the FIRST time the identity lookup fails or stalls.
 *
 * Decided 3's second half: "the blocks stay until the connection returns, THEN the items fill in".
 * After the error arm fires the subscription is complete, so holding the blocks without this would
 * hold them for ever - including after the connection came back, which fails Johanna's sentence as
 * surely as clearing them fails its first half.
 *
 * A TIMER RATHER THAN AN `online` LISTENER, deliberately. `online` fires for a network interface
 * coming back and says nothing about a server that was down, and it is not reachable from the
 * fetch-level simulation these branches are proved with - so it would have shipped unexercised. A
 * timer covers both causes and is the one this round measured end to end.
 *
 * THE CADENCE IS NOT ONE NUMBER (#1197 review F1). `timeout` sits UPSTREAM of `retry`, so a cut
 * connection rejects in milliseconds and the next attempt is this constant later, while a server
 * that HANGS burns {@link RAIL_IDENTITY_TIMEOUT_MS} first and only then waits - and from the second
 * failure on, the wait itself grows towards {@link RAIL_IDENTITY_RETRY_CEILING_MS}.
 */
export const RAIL_IDENTITY_RETRY_MS = 5_000;

/**
 * The longest the rail ever waits between attempts (#1197 review F3).
 *
 * `retry` here has NO `count` and never will: Decided 3 says "the blocks stay until the connection
 * returns", so giving up is not an option the rail has. What was missing was any bound on the COST
 * of not giving up - a fixed five-second interval is ~720 attempts an hour against a server that is
 * down, for as long as the page is open, including in a backgrounded tab. Backing the delay off to
 * a minute keeps the first recovery exactly as fast as it was (attempt 2 is still
 * {@link RAIL_IDENTITY_RETRY_MS} after the first failure) and makes a long outage cost one attempt
 * a minute instead of twelve.
 */
export const RAIL_IDENTITY_RETRY_CEILING_MS = 60_000;

/**
 * The rows the loaded Team section INJECTS beside `teamPrimaryItems` - `Assigned to me` in primary and `Following` in View more. Assigned is emitted
 * inside the loop after index 0 because that is where frame 120-133329 draws it.
 *
 * It is a template element rather than an array entry, so it cannot be counted by reading an array.
 * Named here so the pending menu's row count is `teamPrimaryItems` plus this rather than a total typed
 * out by hand, which is how the skeleton came to draw 2 rows where 3 arrive (#1194 review F2).
 */
const TEAM_INJECTED_ROWS = { primary: 1, more: 1 };

@Component({
  selector: 'app-notes-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.less'],
  standalone: false,
})
export class NotesSideBarComponent
  extends BaseSidebarComponent<Notebook, Note, VirtualFolder>
  implements OnInit, OnDestroy
{
  protected readonly workspaceColor = workspaceColor;
  protected readonly workspacePalette = workspacePalette;
  public readonly SidebarSplitViewKey = SidebarSplitViewKey;
  application = Application.notes;
  sidebarSplitViewKey = SidebarSplitViewKey.notes;
  selfDragDataTypes = [DragDataTypes.note, DragDataTypes.notebook];

  public baseItems: SidebarItem[] = [
    { title: 'Notes', id: 'all_notes', icon: 'sapphire-note' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
    { title: 'Follow Up', id: 'followed', icon: 'sapphire-follow-up' },
    { title: 'Snoozed', id: 'snoozed', icon: 'sapphire-snooze' },
    { title: 'Archive', id: 'archived', icon: 'sapphire-archive' },
    { title: 'Trash', id: 'deleted', icon: 'sapphire-trash' },
  ];

  /**
   * Frame 120-133329 draws two rows above `View more` in each section, and labels the first one
   * `My Notes` under My Workspace against `Notes` under the team's — so this is a relabel of the same
   * `all_notes` filter, not a second filter. `baseItems` stays untouched: the personal rail renders it
   * verbatim, which is the product rule ("Notes app should look as is only if I'm in a private
   * workspace").
   */
  public readonly primaryItems: SidebarItem[] = [
    { title: 'My Notes', id: 'all_notes', icon: 'sapphire-note' },
    { title: 'Flagged', id: 'flagged', icon: 'sapphire-flagged' },
  ];
  /**
   * Frame 102-94461 draws two rows in `Shared`, not the placeholder's three samples: plain nav items at
   * depth 0, so the geometry is `My Notes`'s. What they list is a later round — these are drawn, inert
   * and wired to nothing (#1037 r3 ROW 1).
   */
  public readonly sharedItems: readonly SidebarSharedRow[] = [
    { title: 'Shared Notes', icon: 'sapphire-note' },
    { title: 'Shared Notebooks', icon: 'sapphire-notebook' },
  ];

  public readonly teamPrimaryItems: SidebarItem[] = this.baseItems.filter(({ id }) =>
    ['all_notes', 'flagged'].includes(id),
  );
  public readonly moreItems: SidebarItem[] = this.baseItems.filter(({ id }) => !['all_notes', 'flagged'].includes(id));

  /**
   * One entry per row the loaded Team section draws, and the only thing the pending menu loops over for
   * it: `teamPrimaryItems` plus the primary part of {@link TEAM_INJECTED_ROWS}. Derived from the rows themselves, so a round
   * that adds or drops a team row moves the blocks with it.
   */
  protected readonly teamSkeletonRows: readonly number[] = Array.from(
    { length: this.teamPrimaryItems.length + TEAM_INJECTED_ROWS.primary },
    (_, index) => index,
  );

  /** Following is injected after Snoozed only when View more is expanded. */
  protected readonly teamMoreSkeletonRows: readonly number[] = Array.from(
    { length: this.moreItems.length + TEAM_INJECTED_ROWS.more },
    (_, index) => index,
  );

  protected readonly myWorkspaceExpanded = signal(true);
  protected readonly moreExpanded = signal(false);
  protected readonly teamWorkspaceExpanded = signal(true);
  protected readonly currentTeamWorkspaceExpanded = signal(true);
  protected readonly teamMoreExpanded = signal(false);
  protected currentTeamWorkspace: Space | undefined;
  /**
   * A SIGNAL, not the plain boolean this was until #1180.
   *
   * The skeleton is drawn by an OnPush component behind a signal-based delay helper, and a plain field
   * mutating inside a subscription notifies nothing - the blocks would simply never appear, while
   * every fake-timer spec written against them stayed green. Converting it here rather than keeping
   * the host on default change detection is the clean move: the two writers below are the only ones,
   * and the template reads it in two places.
   */
  protected readonly identityResolved = signal(false);

  /** The one source this rail genuinely owns; see {@link railSkeleton}. */
  protected readonly identityPending = computed<boolean>(() => !this.identityResolved());

  /**
   * Whether the menu's blocks are drawn - true only once identity has been pending for the shared
   * threshold, false the instant it lands.
   *
   * ONE SOURCE IN ROUND 1, AND THAT IS MEASURED RATHER THAN A SIMPLIFICATION. The other three things
   * the rail waits for cannot be bound here yet: the notebooks fetch lives in
   * `BaseSidebarContainersTreeComponent.ngAfterViewInit`, reached through `app-side-bar-notebooks` in
   * the sibling `components/side-bar-notebooks/`, and knots and tags page themselves inside
   * `knowledge-items-rail/` - three directories this round does not own, none of which exposes a
   * pending flag today. It costs nothing visible here, because a section does not render at all until
   * identity resolves, so in notes the headers and the rows under them share one source anyway. The
   * wiring is one block to one source with no shared flag, which is what the other five apps copy and
   * what a later round extends to those three.
   */
  protected readonly railSkeleton = pendingAfterDelay(this.identityPending);
  protected currentMemberMailbox: Team | undefined;
  protected currentTeamMailbox: Team | undefined;
  protected currentTeamWorkspaceInitials = '';

  /** The Pinned / Team Knots and Tags rows' menus: the prototype's entry, then Notes' own Add and Manage. */
  protected readonly pinnedMenus = {
    mine: { knots: notesPinnedMenu('knots', 'mine'), tags: notesPinnedMenu('tags', 'mine') },
    team: { knots: notesPinnedMenu('knots', 'team'), tags: notesPinnedMenu('tags', 'team') },
  } as const;

  protected readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountService = inject(AccountService);
  private readonly spacesService = inject(SpacesService);
  private readonly notesTenantScopeService = inject(NotesTenantScopeService);
  protected readonly assignmentFeed = inject(NoteAssignmentFeedService, { optional: true });
  private readonly crossWorkspaceAlertService = inject(AlertService);
  private dropTenantId: string | undefined;
  private dropDestinationLabel: string | undefined;

  constructor(
    injector: Injector,
    private notesService: NotesService,
    private notebooksService: NotebooksService,
  ) {
    super(injector);
  }

  public override ngOnInit(): void {
    super.ngOnInit();

    if (!this.spaceContextStore.insideTeam()) {
      // A private profile has no team to resolve, so this rail settles on its FIRST paint instead of
      // showing "Loading workspaces…" while it asks the server for a list of teams the user is not in
      // (#1127 seam round 2). The settle is the shipped one, with an empty list: identity resolved, no
      // team, `resolveMemberIdentity(null)`. Deliberately NOT `degradeToPersonal()` — that is the
      // FAILED-lookup arm (invariant 3) and marks this context degraded, which a personal profile is
      // not. The workspace switcher still warms the same list for its own trigger, so nothing else
      // loses its data; the rail simply stops asking a question it already knows the answer to.
      this.setCurrentTeamWorkspace([], this.spaceContextStore.contextId());

      return;
    }

    this.accountService
      .getAccount(true)
      .pipe(
        switchMap((account) =>
          this.spacesService
            .listWithInvites({ archived: false, limit: 100 }, { account })
            .pipe(map(({ items }) => items)),
        ),
        timeout({ first: RAIL_IDENTITY_TIMEOUT_MS }),
        // RELEASE THE CONTENT GATE, THEN ASK AGAIN - and never settle the rail (#1180 ROW 3).
        // `tap` before `retry` so every failed attempt frees the note list, and `retry` before
        // `takeUntil` so destroying the rail stops the loop rather than leaving it asking.
        tap({ error: () => this.releaseContentGate() }),
        // Capped backoff, never a `count` (#1197 review F3): 5 s, 10 s, 20 s, 40 s, then a minute
        // for ever. Attempt 2 is as fast as it ever was, and an outage nobody is watching costs one
        // request a minute rather than twelve.
        retry({
          delay: (_, attempt) =>
            timer(Math.min(RAIL_IDENTITY_RETRY_MS * 2 ** (attempt - 1), RAIL_IDENTITY_RETRY_CEILING_MS)),
        }),
        takeUntil(this.alive),
      )
      .subscribe({
        next: (items) => this.setCurrentTeamWorkspace(items, this.spaceContextStore.contextId()),
      });
  }

  /**
   * Frees the note list after a failed identity lookup, and settles NOTHING on the rail.
   *
   * THIS USED TO DO TWO THINGS AND ONLY ONE OF THEM WAS EVER THE RAIL'S (#1194 review F8, #1180
   * ROW 3). Beside this call it set `identityResolved` and cleared the four workspace fields, which
   * killed the blocks AND sent the template down its `!currentTeamWorkspace` arm - a flat personal
   * rail with no section headings at all. So the load that held the skeleton longest ended by
   * replacing three heading blocks with a rail that has none, and a load that failed FAST never drew
   * a block at all: measured offline at the base, the rail was already flat and sectionless 30 ms in,
   * before the 300 ms threshold. Johanna, Decided 3: "the blocks stay until the connection returns,
   * then the items fill in. There is no error state in this issue."
   *
   * WHAT IS KEPT IS THE HALF THE DEGRADE EXISTS FOR. #1065 r2 gave notes a tenant gate, and without
   * this call the note LIST would sit blank behind the rail: `AppTenantScopeService.degradeToPersonal`
   * marks this context degraded and ticks `degradeChanged`, so `selectedTeamId()` emits instead of
   * withholding. That service bounds itself as well - its own `TENANT_IDENTITY_TIMEOUT_MS` falls
   * through to the STORED value - so this call is the faster release, and a different one, not the
   * only one.
   *
   * IT IS SAFE ON EVERY RETRY. The scope service refuses a context it has already resolved or
   * degraded, so the second and later failures are no-ops; and when an attempt finally succeeds,
   * `resolveMemberIdentity` releases the identity, which deletes the degraded key and re-emits the
   * real member tenant.
   *
   * THE DRIFT THIS CREATES IS DELIBERATE AND RECORDED: tasking's rail still carries the old
   * both-halves shape, so the two differ until tasking's own round.
   */
  private releaseContentGate(): void {
    this.notesTenantScopeService.degradeToPersonal();
  }

  /**
   * Resolves the three identities the two sections need: the space itself, the caller's member tenant
   * inside it, and the space id as a tenant carrier. Mail resolves the same three the same way
   * (`messages/components/side-bar/side-bar.component.ts`) — the `Team` wrapper is a legacy id
   * carrier, not a team.
   */
  private setCurrentTeamWorkspace(items: Space[], contextId: string | undefined): void {
    this.identityResolved.set(true);
    this.currentTeamWorkspace = items.find(
      ({ currentUserStatus, id }) => currentUserStatus === 'accepted' && id === contextId,
    );
    this.currentMemberMailbox = this.currentTeamWorkspace?.myMailboxTenantId
      ? new Team({ id: this.currentTeamWorkspace.myMailboxTenantId })
      : undefined;
    this.currentTeamMailbox = this.currentTeamWorkspace ? new Team({ id: this.currentTeamWorkspace.id }) : undefined;

    // ONE call, and the rail no longer writes the never-visited default beside it: since #1065 r2 the
    // gate withholds an ABSENT slot as well as the legacy `null`, so the default write and the release
    // are order-dependent and belong together in the scope service. The rail cannot test either value
    // through `currentTeamId` anyway — that comes from the stream the gate withholds (#1049).
    this.notesTenantScopeService.resolveMemberIdentity(this.currentMemberMailbox?.id ?? null);

    this.currentTeamWorkspaceInitials =
      this.currentTeamWorkspace?.title
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase() ?? '';
  }

  /** Expands or collapses the current workspace's notes destinations without changing space context. */
  protected toggleCurrentTeamWorkspace(): void {
    this.currentTeamWorkspaceExpanded.update((expanded) => !expanded);
  }

  public override dndDrop(
    dragData: DragData,
    sidebarFolder: VirtualFolder,
    index?: number,
    team: Team | undefined = this.team,
    destinationLabel?: string,
  ): void {
    this.dropTenantId = team?.id;
    this.dropDestinationLabel = destinationLabel;

    // Only the two types this rail owns actions for reach the honest dialog: a note (live Copy, r17)
    // and a notebook (all three disabled, r18). Round 18 dropped the type test altogether, so every
    // other draggable — a task, file or event dragged out of a note's linked-info list — landed on the
    // same dialog and kept the base's LIVE Copy, which sends a foreign id to `notesService.duplicate`.
    // Anything else falls through to the base rail, which is where that type's drop behaviour lives.
    const ownsCrossWorkspaceDialog = dragData.type === DragDataTypes.note || dragData.type === DragDataTypes.notebook;

    if (ownsCrossWorkspaceDialog && this.isCrossWorkspaceDrop(dragData)) {
      this.crossWorkspaceAlertService.show({
        title: 'Copy, Move or Stitch?',
        body: 'What do we do with the dropped item?',
        note:
          dragData.type === DragDataTypes.notebook
            ? CROSS_WORKSPACE_DROP_TEXT.notebookNote
            : CROSS_WORKSPACE_DROP_TEXT.note,
        rightButtons: this.getCrossWorkspaceDropButtons(dragData),
      });
      return;
    }

    super.dndDrop(dragData, sidebarFolder, index);
  }

  handleMove(dragData: DragData, sidebarFolder: VirtualFolder) {
    const ids = this.getIds(dragData.data);
    const tenantItems = dragData.data.filter(isStitchItemSummary);

    if (sidebarFolder === 'deleted') {
      if (dragData.type === DragDataTypes.note) {
        this.notesService.delete({ ids }, true, undefined, { tenantItems });
      } else if (dragData.type === DragDataTypes.notebook) {
        this.notebooksService.delete({ ids }, true, undefined, { tenantItems });
      }
      return;
    }

    // Restore trashed/archived items first — virtual-folder lists filter out archived/deleted
    // items, so applying the changes alone would leave them stuck in Trash/Archive.
    const restoreOptions = { skipArchiveRestore: sidebarFolder === 'archived' };

    // The dropped type may have no destination on this row at all, and then the drop is a quiet no-op:
    // no request, no toast, no throw. `Notebook` has no answer for `all_notes` and neither type has one
    // for `search_results` — the rail's droppable "All Results" row, present whenever an advanced search
    // is applied — so both statics `break` out of their switch and return `undefined`. Round 18 keyed
    // this on `all_notes` alone, which left the same destructure TypeError alive one row down. The base
    // has `isDropDisabled` for the search row, but it is wired to nothing and does not cover
    // notebook-on-`all_notes`, so the absence of a result is the condition, not the folder id.
    if (dragData.type === DragDataTypes.note) {
      const destination = Note.getChangesForVirtualFolder(sidebarFolder, this.dropDestinationLabel);

      if (!destination) {
        return;
      }

      const { changes, message } = destination;
      moveItemsIntoContainer(
        this.notesService,
        dragData.data,
        () => this.notesService.bunchUpdate({ ids }, changes, { message, tenantItems }),
        restoreOptions,
      );
    } else if (dragData.type === DragDataTypes.notebook) {
      const destination = Notebook.getChangesForVirtualFolder(sidebarFolder);

      if (!destination) {
        return;
      }

      const { changes, message } = destination;
      moveItemsIntoContainer(
        this.notebooksService,
        dragData.data,
        () => this.notebooksService.bunchUpdate({ ids }, changes, { message, tenantItems }),
        restoreOptions,
      );
    }
  }

  handleMore(item: Note) {
    this.stateService.setMainView(item);
  }

  getStitchFromDragData(dragData: DragData, sidebarFolder: VirtualFolder) {
    const { changes } = Note.getChangesForVirtualFolder(sidebarFolder);

    return new Note({ ...changes, title: dragData.data[0]['title'], accountId: this.dropTenantId });
  }

  private getCrossWorkspaceDropButtons(dragData: DragData): AlertButtonConfig[] {
    const tenantItems = dragData.data.filter(isStitchItemSummary);
    const destinationTenantId = this.dropTenantId ?? this.spaceContextStore.contextId();
    const requestContext = mailboxTenantContext(destinationTenantId ?? null);
    if (destinationTenantId) {
      requestContext.set(OWNING_SPACE_ID, destinationTenantId);
    }

    const copyButton: AlertButtonConfig =
      dragData.type === DragDataTypes.notebook
        ? {
            title: 'Copy',
            disabled: true,
            tooltip: CROSS_WORKSPACE_DROP_TEXT.notebookCopyTooltip,
            close: true,
          }
        : {
            title: 'Copy',
            click: () => {
              this.notesService
                .duplicate(this.getIds(dragData.data), undefined, { tenantItems, requestContext })
                .pipe(takeUntil(this.alive))
                .subscribe();
            },
            close: true,
          };

    return [
      { title: 'Cancel', close: true },
      copyButton,
      {
        title: 'Move',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.moveTooltip,
        close: true,
      },
      {
        title: 'Stitch',
        disabled: true,
        tooltip: CROSS_WORKSPACE_DROP_TEXT.stitchTooltip,
        close: true,
      },
    ];
  }

  private isCrossWorkspaceDrop(dragData: DragData): boolean {
    const source = dragData.data[0];
    const sourceTenantId = getStringField(source, 'accountId');
    const targetTenantId = this.dropTenantId ?? this.spaceContextStore.contextId();

    return sourceTenantId !== undefined && targetTenantId !== undefined && sourceTenantId !== targetTenantId;
  }
}
